import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/Stores/auth-v2-store";
import { getUpsellPlatformUrl } from "@/config/domainConfigLoader";
import { Loader2 } from "lucide-react";

export function RequireAuth() {
  const { isAuthReady, isAuthenticated, currentUser, logout } = useAuthStore();
  const location = useLocation();

  // Profil « upsell only » : toutes ses permissions sont upsell.* → il n'a
  // rien à faire dans l'admin, on le renvoie vers la plateforme upsell du
  // même environnement (les hooks doivent précéder tout return conditionnel).
  const perms = currentUser?.permissions || [];
  const isUpsellOnly =
    isAuthenticated &&
    currentUser?.type === 'INTERNAL' &&
    perms.length > 0 &&
    !perms.includes('*') &&
    perms.every((p) => p.startsWith('upsell.'));

  useEffect(() => {
    if (isUpsellOnly) {
      const url = getUpsellPlatformUrl();
      if (url) {
        window.location.replace(url);
      }
    }
  }, [isUpsellOnly]);

  // Support du magic link via hash
  if (location.hash.startsWith("#login_with_access_token=")) {
    const access_token = location.hash.substring("#login_with_access_token=".length);
    return <Navigate to="/login" state={{ from: location, access_token }} replace />
  }

  // Attendre l'hydratation du store
  if (!isAuthReady) {
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
        <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-indigo-600" />
      </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verifier que l'utilisateur est INTERNAL
  if (currentUser.type !== 'INTERNAL') {
    logout();
    return <Navigate to="/login" state={{ error: "Vous n'avez pas les droits d'accès à cette application." }} replace />;
  }

  if (isUpsellOnly) {
    const upsellUrl = getUpsellPlatformUrl();
    return (
      <div className="tw-min-h-screen tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-px-4">
        <div className="tw-max-w-md tw-w-full tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-8 tw-text-center">
          <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-2">Compte réservé à la plateforme UpSell</h1>
          <p className="tw-text-gray-600 tw-mb-6">
            {upsellUrl
              ? 'Redirection vers la plateforme UpSell…'
              : "Votre compte n'a accès qu'à la plateforme UpSell. Connectez-vous depuis son adresse dédiée."}
          </p>
          {upsellUrl && (
            <a
              href={upsellUrl}
              className="tw-block tw-w-full tw-px-4 tw-py-2 tw-mb-3 tw-bg-blue-600 tw-text-white tw-font-medium tw-rounded-md hover:tw-bg-blue-700"
            >
              Ouvrir la plateforme UpSell
            </a>
          )}
          <button
            onClick={() => logout()}
            className="tw-w-full tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-font-medium tw-rounded-md hover:tw-bg-gray-300"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
